import { useMemo } from "react";
import type { NavItemDataProps } from "@/components/nav/types";
import { GLOBAL_CONFIG } from "@/global-config";
import { useWalletAuth } from "@/routes/hooks/useWalletAuth";
import { useUserPermissions } from "@/store/userStore";
import { checkAny } from "@/utils";
import { backendNavData } from "./nav-data-backend";
import { frontendNavData } from "./nav-data-frontend";

const navData = GLOBAL_CONFIG.routerMode === "backend" ? backendNavData : frontendNavData;

/**
 * 递归处理导航数据，过滤掉没有权限的项目
 * @param items 导航项目数组
 * @param permissions 权限列表
 * @param isAuthenticated 是否已登录
 * @returns 过滤后的导航项目数组
 */
const filterItems = (items: NavItemDataProps[], permissions: string[], isAuthenticated: boolean) => {
	return items.filter((item) => {
		// 如果是 portfolio 页面且未登录，则过滤掉
		if (item.path === "/profile" && !isAuthenticated) {
			return false;
		}

		// 检查当前项目是否有权限
		const hasPermission = item.auth ? checkAny(item.auth, permissions) : true;

		// 如果有子项目，递归处理
		if (item.children?.length) {
			const filteredChildren = filterItems(item.children, permissions, isAuthenticated);
			// 如果子项目都被过滤掉了，则过滤掉当前项目
			if (filteredChildren.length === 0) {
				return false;
			}
			// 更新子项目
			item.children = filteredChildren;
		}

		return hasPermission;
	});
};

/**
 *
 * 根据权限过滤导航数据
 * @param permissions 权限列表
 * @param isAuthenticated 是否已登录
 * @returns 过滤后的导航数据
 */
const filterNavData = (permissions: string[], isAuthenticated: boolean) => {
	return navData
		.map((group) => {
			// 过滤组内的项目
			const filteredItems = filterItems(group.items, permissions, isAuthenticated);

			// 如果组内没有项目了，返回 null
			if (filteredItems.length === 0) {
				return null;
			}

			// 返回过滤后的组
			return {
				...group,
				items: filteredItems,
			};
		})
		.filter((group): group is NonNullable<typeof group> => group !== null); // 过滤掉空组
};

/**
 * Hook to get filtered navigation data based on user permissions and authentication status
 * @returns Filtered navigation data
 */
export const useFilteredNavData = () => {
	const permissions = useUserPermissions();
	const { isAuthenticated } = useWalletAuth();
	const permissionCodes = useMemo(() => permissions.map((p) => p.code), [permissions]);
	const authenticated = isAuthenticated();
	const filteredNavData = useMemo(
		() => filterNavData(permissionCodes, authenticated),
		[permissionCodes, authenticated],
	);
	return filteredNavData;
};
